import * as React from "react";
import {Grid2 as Grid} from "@mui/material";
import {OrderDocumentDTO, OrderDocumentsDTO, OrderDocumentType, RequestActions} from "@/api/data-contracts";
import {ChangeHandler, ModelAction} from "@/lib/modelstate";
import {FieldError} from "@/lib/utils";
import {CardEx} from "@/lib/components/CardEx";
import {OrderDocuments} from "@/common/OrderDocuments";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {IconButtonEx} from "@/lib/components/buttons/IconButtonEx";
import EditIcon from "@mui/icons-material/Edit";
import {Constants} from "@/api/constants";
import {useTranslation} from "react-i18next";
import {RequestI18N} from "@/translations";
import {OrderDataType} from "@/model/orderData";

type FieldState = {
    isAdditionalInfoReadOnly: boolean;
    isAdditionalInfoDisabled: boolean;
};

/**
 * Resolves the read-only / disabled state of the additional-information field.
 *
 * Semantics (mirrors the original PricingCard boolean cascade exactly):
 * - "request" + not readonly: field is directly editable; readOnly=false, disabled=false.
 * - Any other mode, or "request" + readonly: the field is constrained.
 *   - "quote" mode OR canEditAddInfo: shown as read-only (styled, still interactive — edit icon visible).
 *   - Otherwise: greyed-out disabled.
 */
function resolveFieldState(mode: OrderDataType, isReadonly: boolean, canEditAddInfo: boolean): FieldState {
    // Any mode other than "request" is always treated as readonly (mirrors `mode !== "request"` guard).
    const isEffectivelyReadonly = isReadonly || mode !== "request";

    switch (true) {
        case !isEffectivelyReadonly:
            // "request" mode + not externally readonly: fully editable.
            return {isAdditionalInfoReadOnly: false, isAdditionalInfoDisabled: false};
        case mode === "quote":
            // Supplier view: read-only styled (not greyed-out), never disabled.
            return {isAdditionalInfoReadOnly: true, isAdditionalInfoDisabled: false};
        case canEditAddInfo:
            // Buyer can open the edit dialog: show read-only with edit icon, not greyed-out.
            return {isAdditionalInfoReadOnly: true, isAdditionalInfoDisabled: false};
        default:
            // All remaining cases (accept, other modes, or request+readonly without edit permission):
            // greyed-out disabled, no edit icon.
            return {isAdditionalInfoReadOnly: false, isAdditionalInfoDisabled: true};
    }
}

export type OrderDocumentsCardProps = {
    orderDocuments?: OrderDocumentsDTO;
    dispatch?: React.Dispatch<ModelAction>;
    readonly?: boolean;
    errors?: { orderDocuments?: { bluePrints?: string; printImages?: string }; additionalInformation?: FieldError };
    additionalInformation?: string | null;
    additionalInformationError?: FieldError;
    onChange?: ChangeHandler;
    mode?: OrderDataType;
    allowedActions?: RequestActions[];
    onEditAdditionalInformation?: () => void;
    /** When true, the additional-information field is always rendered even if the value is null/empty. */
    showAdditionalInformation?: boolean;
    /**
     * When true, applies the emphasized styling (thicker border, slightly larger label) to the
     * additional-information field. Call-site-scoped opt-in — defaults to false so all existing
     * callers (including the supplier view) remain visually unchanged.
     */
    emphasized?: boolean;
};

/**
 * Displays the "Dokumente und sonstige Informationen" card.
 * Contains document uploads (blueprints and print images) and, when the optional
 * additionalInformation props are provided, the "Sonstige Informationen" free-text field below them.
 * If neither `onChange` nor `additionalInformation` is provided the additional-information section
 * is not rendered, keeping backwards-compatibility for callers that do not need it.
 */
export const OrderDocumentsCard = ({
                                       orderDocuments,
                                       dispatch,
                                       readonly,
                                       errors,
                                       additionalInformation,
                                       additionalInformationError,
                                       onChange,
                                       mode = "request",
                                       allowedActions,
                                       onEditAdditionalInformation,
                                       showAdditionalInformation = false,
                                       emphasized = false
                                   }: OrderDocumentsCardProps) => {
    const {t} = useTranslation();

    const onFileAdded = (files: OrderDocumentDTO[]) => {
        dispatch?.({ type: "setDocuments", data: files, nochange: true });
    };

    const onFileRemoved = (file: OrderDocumentDTO) => {
        dispatch?.({ type: "remDocument", data: file, nochange: true });
    };

    const showAdditionalInfo = showAdditionalInformation || onChange != null || additionalInformation != null;
    const canEditAddInfo = allowedActions?.includes(RequestActions.UPDATE_ADDITIONAL_INFORMATION) === true;
    const {isAdditionalInfoReadOnly, isAdditionalInfoDisabled} = resolveFieldState(mode, !!readonly, canEditAddInfo);

    /**
     * Narrows the broad `ChangeHandler` union to the specific signature expected by `StringInputField`.
     * `StringInputField` only ever emits a `React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>`,
     * which is assignment-compatible with the wider `ChangeHandler` — the adapter simply threads it through.
     */
    const handleAdditionalInfoChange: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (e) =>
            onChange?.(e);

    return (
            <CardEx title={t(RequestI18N.details.field.documentsAndAdditionalInformation)}>
            <Grid container spacing={2} direction="column">
                <Grid size={{ sm: 12 }}>
                    <OrderDocuments
                            title={t(RequestI18N.details.field.constructionDrawings)}
                        orderDocuments={orderDocuments}
                        type={OrderDocumentType.BLUE_PRINT}
                        onFileAdded={onFileAdded}
                        onFileRemoved={onFileRemoved}
                        disabled={readonly}
                        error={errors?.orderDocuments?.bluePrints}
                    />
                </Grid>
                <Grid size={{ sm: 12 }}>
                    <OrderDocuments
                            title={t(RequestI18N.details.field.printImages)}
                        orderDocuments={orderDocuments}
                        type={OrderDocumentType.PRINT_IMAGE}
                        onFileAdded={onFileAdded}
                        onFileRemoved={onFileRemoved}
                        disabled={readonly}
                        error={errors?.orderDocuments?.printImages}
                    />
                </Grid>
                {showAdditionalInfo && (
                        <Grid size={{xs: 12}}>
                            <StringInputField
                                    name="additionalInformation"
                                    label={
                                        mode === "quote"
                                                ? t(RequestI18N.details.field.additionalInformationSupplier)
                                                : t(RequestI18N.details.field.additionalInformationBuyer)
                                    }
                                    value={additionalInformation ?? undefined}
                                    onChange={handleAdditionalInfoChange}
                                    error={additionalInformationError}
                                    multiline
                                    minRows={5}
                                    maxChars={Constants.REQUEST_ADDITIONAL_INFORMATION_LENGTH_MAX}
                                    disabled={isAdditionalInfoDisabled}
                                    readOnly={isAdditionalInfoReadOnly}
                                    emphasized={emphasized}
                                    endAdornment={
                                        canEditAddInfo && onEditAdditionalInformation != null ? (
                                                <IconButtonEx
                                                        size="small"
                                                        Icon={EditIcon}
                                                        title={t(RequestI18N.details.action.editAdditionalInformation)}
                                                        onClick={onEditAdditionalInformation}
                                                />
                                        ) : undefined
                                    }
                            />
                        </Grid>
                )}
            </Grid>
        </CardEx>
    );
};
